"use client";

import React, { useEffect, useRef } from "react";

// How much slower each layer moves relative to normal scroll speed.
// 0 = fixed in place, 1 = scrolls at normal speed.
const BG_SPEED = 0.4;
const CONTENT_SPEED = 0.15;

const ParallexPune = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const bg = bgRef.current;
    const content = contentRef.current;
    if (!section || !bg || !content) return;

    let ticking = false;

    const updateParallax = () => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (rect.bottom < 0 || rect.top > viewportHeight) {
        ticking = false;
        return;
      }

      const scrolled = -rect.top;
      const bgOffset = scrolled * BG_SPEED;
      const contentOffset = scrolled * CONTENT_SPEED;

      bg.style.transform = `translate3d(0, ${bgOffset}px, 0)`;
      content.style.transform = `translate3d(0, ${contentOffset}px, 0)`;

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateParallax();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <>
      {/* Desktop: JS-driven parallax, background and content drift at different rates */}
      <section
        ref={sectionRef}
        className="hidden md:flex relative w-full h-[80vh] justify-center overflow-hidden bg-white pt-[125px]"
      >
        <div
          ref={bgRef}
          className="absolute left-0 top-0 h-[150%] w-full bg-cover bg-center bg-no-repeat [will-change:transform]"
          style={{
            backgroundImage:
              "url(https://ik.imagekit.io/mileseducation/miles_website/City/pune/paralexPuneBGpc.webp)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/10" />
        <div
          ref={contentRef}
          className="relative z-[2] w-full max-w-[625px] text-center [will-change:transform]"
        >
          <h2 className="mb-4 text-[32px] font-bold leading-tight [text-shadow:0_2px_12px_rgba(255,255,255,0.8)]">
            <span className="text-[#1b1b1b]">Why </span>
            <span className="text-[#39F]">CPA Makes Sense in Pune</span>
          </h2>
          <p className="text-sm leading-relaxed text-[#1b1b1b]/85 [text-shadow:0_1px_8px_rgba(255,255,255,0.8)]">
            Pune is one of India&apos;s largest automotive and manufacturing
            hubs, increasingly paired with a fast-growing IT and GCC layer. A
            CPA is what connects that industrial base to global finance and
            reporting work.
          </p>
        </div>
      </section>

      {/* Mobile: static card, image below the copy, no parallax */}
      <section className="md:hidden w-full px-4 pt-6 bg-[#f3f3f3]">
        <div className="w-full overflow-hidden rounded-[28px] bg-white shadow-md">
          <div className="px-4 pt-8 pb-2 text-center">
            <h2 className="mb-4 text-2xl font-bold leading-tight">
              <span className="text-[#061A2F]">Why </span>
              <span className="text-[#39F]">
                CPA Makes Sense
                <br />
                in Pune
              </span>
            </h2>
            <p className="mx-auto max-w-[320px] text-sm font-medium leading-tight text-[#000]">
              Pune is one of India&apos;s largest automotive and manufacturing
              hubs, increasingly paired with a fast-growing IT and GCC layer. A
              CPA is what connects that industrial base to global finance and
              reporting work.
            </p>
          </div>
          <img
            src="https://ik.imagekit.io/mileseducation/miles_website/City/pune/paralexPuneBGmob.webp"
            alt="Why CPA makes sense in Pune"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>
    </>
  );
};

export default ParallexPune;
